// src/components/production/CostBreakdown.jsx

import React from 'react';
import { formatCurrency } from '../../utils/costCalculationUtils';

const styles = {
    container: {
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    title: {
        fontSize: '18px',
        fontWeight: '600',
        margin: 0
    },
    categorySection: {
        marginBottom: '20px'
    },
    categoryHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid #e2e8f0'
    },
    categoryTitle: {
        fontSize: '16px',
        fontWeight: '600',
        margin: 0
    },
    categoryTotal: {
        fontSize: '16px',
        fontWeight: '700'
    },
    itemRow: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 1fr',
        gap: '15px',
        padding: '10px 0',
        borderBottom: '1px solid #f1f5f9'
    },
    itemLabel: {
        fontSize: '14px',
        color: '#374151'
    },
    itemValue: {
        fontSize: '14px',
        textAlign: 'right',
        color: '#374151'
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 0',
        borderTop: '2px solid #e2e8f0',
        fontWeight: '700',
        fontSize: '16px'
    },
    positive: {
        color: '#10b981'
    },
    negative: {
        color: '#ef4444'
    }
};

function CostBreakdown({ costData, productName, quantity }) {
    const unitCost = costData.totals.total / (quantity || 1);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h3 style={styles.title}>{productName} - Detaylı Maliyet Analizi</h3>
                <div style={{ fontSize: '16px', fontWeight: '700' }}>
                    Birim Maliyet: {formatCurrency(unitCost)}
                </div>
            </div>

            <div style={styles.categorySection}>
                <div style={styles.categoryHeader}>
                    <h4 style={styles.categoryTitle}>Hammadde Maliyeti</h4>
                    <div style={{...styles.categoryTotal, color: '#0f172a'}}>
                        {formatCurrency(costData.totals.rawMaterials)}
                    </div>
                </div>
                
                {costData.breakdown.rawMaterials.map((item, index) => (
                    <div key={index} style={styles.itemRow}>
                        <div style={styles.itemLabel}>
                            {item.materialName} ({item.stageName})
                        </div>
                        <div style={styles.itemValue}>
                            {item.quantity} {item.unit}
                        </div>
                        <div style={styles.itemValue}>
                            {formatCurrency(item.unitPrice)}
                        </div>
                        <div style={{...styles.itemValue, fontWeight: '600'}}>
                            {formatCurrency(item.total)}
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.categorySection}>
                <div style={styles.categoryHeader}>
                    <h4 style={styles.categoryTitle}>Fason Maliyeti</h4>
                    <div style={{...styles.categoryTotal, color: '#0f172a'}}>
                        {formatCurrency(costData.totals.subcontracting)}
                    </div>
                </div>
                
                {costData.breakdown.subcontracting.length > 0 ? (
                    costData.breakdown.subcontracting.map((item, index) => (
                        <div key={index} style={styles.itemRow}>
                            <div style={styles.itemLabel}>
                                {item.subcontractorName} ({item.stageName})
                            </div>
                            <div style={styles.itemValue}>
                                {item.quantity} {item.unit}
                            </div>
                            <div style={styles.itemValue}>
                                {formatCurrency(item.unitPrice)}
                            </div>
                            <div style={{...styles.itemValue, fontWeight: '600'}}>
                                {formatCurrency(item.total)}
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ padding: '15px 0', textAlign: 'center', color: '#64748b' }}>
                        Fason maliyeti bulunmamaktadır
                    </div>
                )}
            </div>

            <div style={styles.categorySection}>
                <div style={styles.categoryHeader}>
                    <h4 style={styles.categoryTitle}>İşçilik Maliyeti</h4>
                    <div style={{...styles.categoryTotal, color: '#0f172a'}}>
                        {formatCurrency(costData.totals.labor)}
                    </div>
                </div>
                
                {costData.breakdown.labor.map((item, index) => (
                    <div key={index} style={styles.itemRow}>
                        <div style={styles.itemLabel}>
                            {item.stageName}
                        </div>
                        <div style={styles.itemValue}>
                            {item.hours} saat
                        </div>
                        <div style={styles.itemValue}>
                            {formatCurrency(item.hourlyRate)}/saat
                        </div>
                        <div style={{...styles.itemValue, fontWeight: '600'}}>
                            {formatCurrency(item.total)}
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.categorySection}>
                <div style={styles.categoryHeader}>
                    <h4 style={styles.categoryTitle}>Genel Giderler</h4>
                    <div style={{...styles.categoryTotal, color: '#0f172a'}}>
                        {formatCurrency(costData.totals.overhead)}
                    </div>
                </div>
                
                {costData.breakdown.overhead.map((item, index) => (
                    <div key={index} style={styles.itemRow}>
                        <div style={styles.itemLabel}>
                            {item.description}
                        </div>
                        <div style={styles.itemValue}>
                            {item.baseAmount}
                        </div>
                        <div style={styles.itemValue}>
                            {item.rate}
                        </div>
                        <div style={{...styles.itemValue, fontWeight: '600'}}>
                            {formatCurrency(item.total)}
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.totalRow}>
                <div>Toplam Maliyet ({quantity} adet için)</div>
                <div style={{ color: '#0f172a' }}>
                    {formatCurrency(costData.totals.total)}
                </div>
            </div>

            <div style={{...styles.totalRow, fontSize: '18px'}}>
                <div>Birim Maliyet</div>
                <div style={{ color: '#0f172a' }}>
                    {formatCurrency(unitCost)}
                </div>
            </div>
        </div>
    );
}

export default CostBreakdown;